import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import logoBrik from '../assets/logo.png';
import './AuthShell.css';

interface AuthShellProps {
  children: ReactNode;
  /**
   * O bloco de 720px do guia de boas-vindas (28/10/2026) — a mesma coluna da
   * Home. Só /bem-vindo usa; login, cadastro e senha continuam em 436px.
   */
  largo?: boolean;
}

/**
 * A casca de /login, /cadastro e das telas de senha.
 *
 * **Direção B, escolhida em 2026-09-18** (`docs/designsystem/propostas/login.html`):
 * o formulário mora num bloco delimitado por filete, centrado na tela. Não é o
 * cartão flutuante de antes — sem sombra, sem gradiente, sem cor de fundo: é o
 * mesmo recurso que o `lk-table` usa para se ler como bloco sobre a superfície
 * branca.
 *
 * **A faixa de cima saiu** a pedido dele, e com ela a marca e o "Não tem conta?".
 * Os dois desceram para dentro do bloco — a marca no alto, o convite no rodapé
 * do bloco. Sem a faixa, o bloco é a única coisa na tela e não disputa com nada.
 *
 * O que motivou a mudança: na casca anterior o login preenchia 43% da área útil
 * e deixava 384px de vão embaixo, porque a casca tinha sido desenhada com o
 * cadastro à vista (81%). O bloco centrado resolve os dois tamanhos: o branco em
 * volta vira moldura em vez de sobra.
 */
const AuthShell = ({ children, largo = false }: AuthShellProps) => {
  /* Tema claro sempre, em todas as telas de entrada. O tema anterior volta ao
     sair, senão quem usa o app no escuro e passa pelo login leva o claro para
     dentro. */
  useEffect(() => {
    const anterior = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (anterior) document.documentElement.setAttribute('data-theme', anterior);
    };
  }, []);

  return (
    <div className={`au-root${largo ? ' is-largo' : ''}`}>
      <main className="au-body">
        <div className="au-bloco">
          <Link to="/login" className="au-marca" aria-label="Brik CRM">
            <img src={logoBrik} alt="Brik CRM" className="au-logo" />
          </Link>
          {children}
        </div>
      </main>

      <footer className="au-foot">
        {/* As páginas ainda não existem — ele disse em 13/09 que inclui depois.
            Os links já entram apontando para os destinos combinados. */}
        <Link to="/termos">Termos de Uso</Link>
        <Link to="/privacidade">Política de Privacidade</Link>
        {/* O crédito já existia no rodapé do login antigo. O desenho não previa,
            mas função só some quando ele pede — então mudou de lugar, não de
            existência. */}
        <span className="au-foot-right">
          Powered by{' '}
          <a href="https://gbcode.com.br/" target="_blank" rel="noopener noreferrer">GBCode</a>
        </span>
      </footer>
    </div>
  );
};

export default AuthShell;
